import { useRef } from "react";
import useReveal from "@/hooks/useReveal";

const STEPS = [
  { no: "01", title: "Discovery", copy: "An intimate study of your property, audience and service rituals." },
  { no: "02", title: "Brand Immersion", copy: "Decoding architecture, F&B and identity to translate into cloth." },
  { no: "03", title: "Moodboards", copy: "Curated direction in fabric, silhouette, palette and detailing." },
  { no: "04", title: "Sampling", copy: "Refined prototypes, refined again until every stitch agrees." },
  { no: "05", title: "Measurements", copy: "On-property sizing systems for every department and role." },
  { no: "06", title: "Production", copy: "In-house production with disciplined quality control protocols." },
  { no: "07", title: "Rollout", copy: "Coordinated dispatch and fit-out across cities and properties." },
  { no: "08", title: "Replenishment", copy: "Long-horizon replenishment partnerships and seasonal additions." },
];

export default function Process() {
  const eyebrowRef = useReveal();
  const headRef = useReveal();
  const trackRef = useRef(null);

  return (
    <section
      data-testid="process-section"
      className="py-28 md:py-40 lg:py-48 bg-espresso text-bone relative overflow-hidden"
    >
      <div className="container-luxury">
        <div ref={eyebrowRef} className="reveal flex items-center gap-4 mb-8">
          <span className="h-px w-10 bg-champagne" />
          <span className="text-[11px] uppercase tracking-[0.32em] text-champagne font-medium">
            The Auraa Process
          </span>
        </div>

        <div ref={headRef} className="reveal max-w-4xl">
          <h2 className="font-serif text-[40px] md:text-[56px] lg:text-[72px] leading-[1.04] tracking-[-0.015em] text-bone text-balance">
            Eight steps. One uniform programme,
            <em className="italic text-champagne"> rolled out beautifully.</em>
          </h2>
          <p className="mt-7 max-w-xl text-[15px] md:text-[17px] leading-relaxed text-bone/65 font-light">
            A measured cadence engineered for design integrity and operational
            reliability — across single properties and pan-India portfolios.
          </p>
        </div>
      </div>

      {/* Horizontal timeline */}
      <div className="mt-20 md:mt-24 relative">
        {/* horizontal connector */}
        <div
          className="absolute left-0 right-0 top-[88px] md:top-[100px] h-px bg-bone/15 pointer-events-none"
          aria-hidden
        />

        <div
          ref={trackRef}
          className="overflow-x-auto pb-4 scroll-px-6 [scrollbar-width:thin] [scrollbar-color:#C5B39B_transparent]"
        >
          <div className="flex gap-0 px-6 md:px-10 lg:px-16 min-w-max">
            {STEPS.map((s, i) => (
              <TimelineNode key={s.no} s={s} index={i} last={i === STEPS.length - 1} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TimelineNode({ s, index, last }) {
  const ref = useReveal();
  return (
    <div
      ref={ref}
      data-testid={`process-step-${s.no}`}
      className={`reveal reveal-delay-${(index % 4) + 1} relative w-[260px] sm:w-[300px] md:w-[320px] shrink-0 pr-10 md:pr-14 ${
        last ? "pr-0" : ""
      }`}
    >
      {/* number */}
      <div className="font-serif text-[36px] md:text-[44px] leading-none text-champagne tracking-tight">
        {s.no}
      </div>

      {/* node dot */}
      <div className="mt-6 md:mt-7 flex items-center">
        <div className="relative w-3 h-3">
          <span className="absolute inset-0 rounded-full bg-champagne" />
          <span className="absolute -inset-1 rounded-full border border-champagne/40" />
        </div>
        <div className="flex-1 h-px bg-bone/15 ml-2" />
      </div>

      <h3 className="mt-7 font-serif text-[24px] md:text-[28px] tracking-tight text-bone leading-tight">
        {s.title}
      </h3>
      <p className="mt-3 text-[14px] leading-relaxed text-bone/65 font-light max-w-[260px]">
        {s.copy}
      </p>
    </div>
  );
}
